import { useEffect, useId, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { useIntl } from '../../util/reactIntl';

import IconClose from '../IconClose/IconClose';

import css from './ContentModal.module.css';

/**
 * A reusable, trigger-based modal for a short piece of custom content.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.title modal heading
 * @param {React.ReactNode?} props.description modal body copy
 * @param {React.ReactNode?} props.children additional modal content
 * @param {React.ReactNode} props.triggerContent trigger contents
 * @param {string?} props.triggerClassName custom trigger styling
 * @param {string?} props.openLabel accessible label for the trigger
 * @returns {JSX.Element}
 */
const ContentModal = props => {
  const { title, description, children, triggerContent, triggerClassName, openLabel } = props;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const dialogRef = useRef(null);
  const titleId = useId();
  const intl = useIntl();
  const portalRoot =
    typeof document !== 'undefined' ? document.getElementById('portal-root') || document.body : null;
  const closeLabel = intl.formatMessage({ id: 'Modal.closeModal', defaultMessage: 'Close modal' });
  const triggerLabel = openLabel || String(title || '');

  const closeModal = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const openModal = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    dialogRef.current?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName || css.trigger}
        onClick={openModal}
        aria-label={triggerLabel}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        {triggerContent || 'ⓘ'}
      </button>

      {isOpen && portalRoot
        ? ReactDOM.createPortal(
            <div className={css.overlay} role="presentation" onClick={closeModal}>
              <div
                ref={dialogRef}
                className={css.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex="-1"
                onClick={event => event.stopPropagation()}
              >
                <div className={css.header}>
                  <h3 id={titleId} className={css.title}>
                    {title}
                  </h3>
                  <button
                    className={css.close}
                    onClick={closeModal}
                    type="button"
                    aria-label={closeLabel}
                  >
                    <IconClose rootClassName={css.closeIcon} />
                  </button>
                </div>
                <div className={css.content}>
                  {description ? <div className={css.description}>{description}</div> : null}
                  {children}
                </div>
              </div>
            </div>,
            portalRoot
          )
        : null}
    </>
  );
};

export default ContentModal;
